import React from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { TESTIMONIAL_DATA } from "config/testimonals_data";
import UpworkLogo from "assets/images/logo-upwork.png";
import FiverrLogo from "assets/images/logo-fiverr.png";
import { Reveal, StaggerGroup, fadeUp } from "components/ui/motion";
import CountUp from "components/ui/CountUp";
import { motion } from "framer-motion";

const platformLogos = {
  upwork: UpworkLogo,
  fiverr: FiverrLogo,
};

const STATS = [
  { to: 5, decimals: 1, suffix: "★", label: "Average rating" },
  { to: 14, suffix: "+", label: "Verified reviews" },
  { to: 100, suffix: "%", label: "Job success" },
  { display: "Top Rated", label: "On Upwork & Fiverr" },
];

const Stars = ({ rating = 5 }) => (
  <div className="flex gap-0.5 text-amber-400" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={i < rating ? "" : "text-slate-300"}>
        ★
      </span>
    ))}
  </div>
);

const Testimonals = () => {
  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 6000,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    cssEase: "linear",
    pauseOnHover: true,
    swipeToSlide: true,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2, speed: 5000 } },
      { breakpoint: 640, settings: { slidesToShow: 1, speed: 4000 } },
    ],
  };

  return (
    <section className="relative overflow-hidden bg-slate-50 py-20">
      <div className="container-tt">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="eyebrow !text-brand-600 justify-center w-full">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Real client reviews
          </span>
          <h2 className="mt-4 text-3xl font-semibold text-ink sm:text-4xl">
            Trusted by founders &amp; teams worldwide
          </h2>
          <p className="mt-4 text-base text-slate-500">
            Verified five-star reviews from clients on Upwork and Fiverr — for shipping mobile, web, and AI
            products that actually work.
          </p>
        </Reveal>

        {/* trust stats */}
        <StaggerGroup className="mx-auto mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4" stagger={0.1}>
          {STATS.map((s) => (
            <motion.div
              key={s.label}
              variants={fadeUp}
              className="rounded-2xl border border-slate-100 bg-white p-5 text-center shadow-card"
            >
              <div className="bg-brand-gradient bg-clip-text text-2xl font-bold text-transparent">
                <CountUp to={s.to} suffix={s.suffix} decimals={s.decimals} display={s.display} />
              </div>
              <div className="mt-1 text-xs uppercase tracking-wide text-slate-400">{s.label}</div>
            </motion.div>
          ))}
        </StaggerGroup>
      </div>

      <div className="mt-14 [&_.slick-track]:flex">
        <Slider {...settings}>
          {TESTIMONIAL_DATA.map((item, index) => (
            <div key={index} className="h-auto px-3">
              <div className="flex h-full min-h-[260px] flex-col rounded-2xl border border-slate-100 bg-white p-7 shadow-card">
                <div className="mb-4 flex items-center justify-between">
                  <Stars rating={item.rating} />
                  {item.platform && platformLogos[item.platform] && (
                    <span
                      className={`flex items-center rounded-md px-2 py-1 ${
                        item.platform === "upwork" ? "bg-[#14a800]/10" : "bg-[#1dbf73]/10"
                      }`}
                    >
                      <img src={platformLogos[item.platform]} alt={`${item.platform} review`} className="h-4 w-auto" />
                    </span>
                  )}
                </div>
                <p className="flex-grow text-sm leading-relaxed text-slate-600 line-clamp-6">“{item.text}”</p>
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <div className="text-sm font-semibold text-ink">{item.from}</div>
                  <div className="text-xs text-slate-400">{item.designation}</div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
};

export default Testimonals;
